/**
 * Jotai atoms for leads feature state management
 * Manages search state, filters, and pagination
 */

import { atom } from 'jotai';

import { LeadsTableTab } from '@ekhodealer/ekho-common';

import type { Selection } from 'react-aria-components';
import type { SearchLeadsParams } from '../api/useSearchLeads';

import atomWithDebounce from '@common/atoms/atomWithDebounce';
import { getCommonDateRange as getLeadsDateRange } from '@common/lib/filters';
import { getEndOfDay } from '@common/lib/time';
import { CommonDateFilter as LeadsDateFilter } from '@common/types/filters';

// Lead tab filter atom (for Table view tabs)
export const leadTabFilterAtom = atom<LeadsTableTab>(LeadsTableTab.ALL);

// Search value atoms with debouncing
const searchDebounceAtoms = atomWithDebounce('', 500);
export const debouncedSearchValueAtom = searchDebounceAtoms.debouncedValueAtom;
export const searchValueAtom = searchDebounceAtoms.currentValueAtom;

// Pagination atoms
export const currentPageAtom = atom<number>(1);
export const itemsPerPageAtom = atom<number>(25); // Default items per page for table view

// Action atom to handle search value changes
export const updateSearchValueAtom = atom(
	(get) => get(searchValueAtom),
	(get, set, newValue: string) => {
		set(debouncedSearchValueAtom, newValue);
		// Reset to first page when searching
		set(currentPageAtom, 1);
	},
);

// Action required filter atom
export const actionRequiredFilterAtom = atom<boolean>(false);

// Prequal status filter (multi-select: PRE_QUALIFIED, REJECTED, PENDING)
export const preQualStatusFilterAtom = atom<string[]>([]);

// Stage filter (multiple selection)
export const stageFilterAtom = atom<string[]>([]);

// Lead type filter (multiple selection)
export const leadTypeFilterAtom = atom<string[]>([]);

// Vehicle interests/models filter (multiple selection)
export const vehicleModelsFilterAtom = atom<string[]>([]);

// Deal amount range filter
export const dealAmountRangeFilterAtom = atom<{ min: number; max: number } | null>(null);

// Account filter (multiple selection)
export const accountFilterAtom = atom<string[]>([]);

// Date created filter (single selection from predefined ranges)
export const dateCreatedFilterAtom = atom<LeadsDateFilter | null>(null);

// Last updated filter (single selection from predefined ranges)
export const lastUpdatedFilterAtom = atom<LeadsDateFilter | null>(null);

// Custom date created range (Unix ms timestamps, used when dateCreatedFilterAtom === CUSTOM)
export const dateCreatedCustomRangeAtom = atom<{ from: number | null; to: number | null }>({
	from: null,
	to: null,
});

// Custom last updated range (Unix ms timestamps, used when lastUpdatedFilterAtom === CUSTOM)
export const lastUpdatedCustomRangeAtom = atom<{ from: number | null; to: number | null }>({
	from: null,
	to: null,
});

// Pipeline filter (multiple selection, stores LeadsTableTab values)
export const pipelineFilterAtom = atom<string[]>([]);

// Action atom to handle action required filter changes
export const updateActionRequiredFilterAtom = atom(
	(get) => get(actionRequiredFilterAtom),
	(get, set, newValue: boolean) => {
		set(actionRequiredFilterAtom, newValue);
		// Reset to page 1 when filter changes
		set(currentPageAtom, 1);
	},
);

// Action atom to handle prequal status filter changes
export const updatePreQualStatusFilterAtom = atom(
	(get) => get(preQualStatusFilterAtom),
	(get, set, newValue: string[]) => {
		set(preQualStatusFilterAtom, newValue);
		// Reset to page 1 when filter changes
		set(currentPageAtom, 1);
	},
);

// Action atom to handle stage filter changes
export const updateStageFilterAtom = atom(
	(get) => get(stageFilterAtom),
	(get, set, newValue: string[]) => {
		set(stageFilterAtom, newValue);
		// Reset to page 1 when filter changes
		set(currentPageAtom, 1);
	},
);

// Action atom to handle lead type filter changes
export const updateLeadTypeFilterAtom = atom(
	(get) => get(leadTypeFilterAtom),
	(get, set, newValue: string[]) => {
		set(leadTypeFilterAtom, newValue);
		// Reset to page 1 when filter changes
		set(currentPageAtom, 1);
	},
);

// Action atom to handle vehicle models filter changes
export const updateVehicleModelsFilterAtom = atom(
	(get) => get(vehicleModelsFilterAtom),
	(get, set, newValue: string[]) => {
		set(vehicleModelsFilterAtom, newValue);
		// Reset to page 1 when filter changes
		set(currentPageAtom, 1);
	},
);

// Action atom to handle deal amount range filter changes
export const updateDealAmountRangeFilterAtom = atom(
	(get) => get(dealAmountRangeFilterAtom),
	(get, set, newValue: { min: number; max: number } | null) => {
		set(dealAmountRangeFilterAtom, newValue);
		// Reset to page 1 when filter changes
		set(currentPageAtom, 1);
	},
);

// Action atom to handle account filter changes
export const updateAccountFilterAtom = atom(
	(get) => get(accountFilterAtom),
	(get, set, newValue: string[]) => {
		set(accountFilterAtom, newValue);
		// Reset to page 1 when filter changes
		set(currentPageAtom, 1);
	},
);

// Action atom to handle date created filter changes
export const updateDateCreatedFilterAtom = atom(
	(get) => get(dateCreatedFilterAtom),
	(get, set, newValue: LeadsDateFilter | null) => {
		set(dateCreatedFilterAtom, newValue);
		// Clear custom range when switching away from custom
		if (newValue !== LeadsDateFilter.CUSTOM) {
			set(dateCreatedCustomRangeAtom, { from: null, to: null });
		}
		// Reset to page 1 when filter changes
		set(currentPageAtom, 1);
	},
);

// Action atom to handle date created custom range changes
export const updateDateCreatedCustomRangeAtom = atom(
	(get) => get(dateCreatedCustomRangeAtom),
	(get, set, newValue: { from: number | null; to: number | null }) => {
		set(dateCreatedCustomRangeAtom, newValue);
		// Reset to page 1 when filter changes
		set(currentPageAtom, 1);
	},
);

// Action atom to handle pipeline filter changes
export const updatePipelineFilterAtom = atom(
	(get) => get(pipelineFilterAtom),
	(get, set, newValue: string[]) => {
		set(pipelineFilterAtom, newValue);
		// Reset to page 1 when filter changes
		set(currentPageAtom, 1);
	},
);

// Action atom to handle last updated filter changes
export const updateLastUpdatedFilterAtom = atom(
	(get) => get(lastUpdatedFilterAtom),
	(get, set, newValue: LeadsDateFilter | null) => {
		set(lastUpdatedFilterAtom, newValue);
		// Clear custom range when switching away from custom
		if (newValue !== LeadsDateFilter.CUSTOM) {
			set(lastUpdatedCustomRangeAtom, { from: null, to: null });
		}
		// Reset to page 1 when filter changes
		set(currentPageAtom, 1);
	},
);

// Action atom to handle last updated custom range changes
export const updateLastUpdatedCustomRangeAtom = atom(
	(get) => get(lastUpdatedCustomRangeAtom),
	(get, set, newValue: { from: number | null; to: number | null }) => {
		set(lastUpdatedCustomRangeAtom, newValue);
		// Reset to page 1 when filter changes
		set(currentPageAtom, 1);
	},
);

// Derived atom for leads query parameters
// Provides a single source of truth for query parameters used by TanStack Query
export const leadsParamsAtom = atom<SearchLeadsParams>((get) => {
	const searchQuery = get(debouncedSearchValueAtom);
	const tab = get(leadTabFilterAtom);
	const page = get(currentPageAtom);
	const itemsPerPage = get(itemsPerPageAtom);
	const actionRequired = get(actionRequiredFilterAtom);

	const params: SearchLeadsParams = {
		limit: itemsPerPage,
		page: page - 1, // Convert to 0-indexed
		facets: ['*'], // Request all facets that match the query
	};

	if (searchQuery.trim()) {
		params.query = searchQuery.trim();
	}

	// Build filters object
	const filters: SearchLeadsParams['filters'] = {};

	// Add tab filter
	if (tab) {
		filters.tab = tab;
	}

	// Add action required filter if enabled
	if (actionRequired) {
		filters.actionRequired = true;
	}

	// Add prequal status filters
	const preQualStatuses = get(preQualStatusFilterAtom);
	if (preQualStatuses.length > 0) {
		filters.preQualStatus =
			preQualStatuses.length === 1 ? preQualStatuses[0] : [...preQualStatuses];
	}

	// Add stage filters
	const stages = get(stageFilterAtom);
	if (stages.length > 0) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- API filter accepts string | string[], cast needed for union
		filters.stage = stages.length === 1 ? stages[0] : (stages as any);
	}

	// Add lead type filters
	const leadTypes = get(leadTypeFilterAtom);
	if (leadTypes.length > 0) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- API filter accepts string | string[], cast needed for union
		filters.leadType = leadTypes.length === 1 ? leadTypes[0] : (leadTypes as any);
	}

	// Add vehicle models filters
	const vehicleModels = get(vehicleModelsFilterAtom);
	if (vehicleModels.length > 0) {
		filters.models = vehicleModels.length === 1 ? vehicleModels[0] : vehicleModels;
	}

	// Add deal amount range filter
	const dealAmountRange = get(dealAmountRangeFilterAtom);
	if (dealAmountRange) {
		filters.cartTotalRange = {
			gte: dealAmountRange.min,
			lte: dealAmountRange.max,
		};
	}

	// Add account filters
	const accounts = get(accountFilterAtom);
	if (accounts.length > 0) {
		filters.accountFilter = accounts.length === 1 ? accounts[0] : accounts;
	}

	// Add pipeline filters
	const pipelines = get(pipelineFilterAtom);
	if (pipelines.length > 0) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- API filter accepts string | string[], cast needed for union
		filters.pipeline = pipelines.length === 1 ? pipelines[0] : (pipelines as any);
	}

	// Add date created filter (single selection or custom range)
	const dateCreatedFilter = get(dateCreatedFilterAtom);
	if (dateCreatedFilter) {
		if (dateCreatedFilter === LeadsDateFilter.CUSTOM) {
			const customRange = get(dateCreatedCustomRangeAtom);
			if (customRange.from !== null) {
				filters.dateCreatedRange = {
					gte: customRange.from,
					...(customRange.to !== null ? { lte: getEndOfDay(customRange.to) } : {}),
				};
			}
		} else {
			const dateRange = getLeadsDateRange(dateCreatedFilter);
			if (dateRange) {
				filters.dateCreatedRange = dateRange;
			}
		}
	}

	// Add last updated filter (single selection or custom range)
	const lastUpdatedFilter = get(lastUpdatedFilterAtom);
	if (lastUpdatedFilter) {
		if (lastUpdatedFilter === LeadsDateFilter.CUSTOM) {
			const customRange = get(lastUpdatedCustomRangeAtom);
			if (customRange.from !== null) {
				filters.lastUpdatedRange = {
					gte: customRange.from,
					...(customRange.to !== null ? { lte: getEndOfDay(customRange.to) } : {}),
				};
			}
		} else {
			const dateRange = getLeadsDateRange(lastUpdatedFilter);
			if (dateRange) {
				filters.lastUpdatedRange = dateRange;
			}
		}
	}

	if (Object.keys(filters).length > 0) {
		params.filters = filters;
	}

	return params;
});

// Derived atom for base API parameters (without most filters)
// Used for getting facet counts that show total available options
// Only includes search query and tab to provide context, but excludes all other filters
// so counts remain constant regardless of filter selections
export const leadsBaseParamsAtom = atom<SearchLeadsParams>((get) => {
	const searchQuery = get(debouncedSearchValueAtom);
	const tab = get(leadTabFilterAtom);

	const params: SearchLeadsParams = {
		limit: 0, // We only need facets, not data
		page: 0,
		facets: ['*'], // Request all facets that match the query
	};

	if (searchQuery.trim()) {
		params.query = searchQuery.trim();
	}

	// Build filters object (exclude most filters to keep counts stable)
	const filters: SearchLeadsParams['filters'] = {};

	// Add tab filter
	if (tab) {
		filters.tab = tab;
	}

	// Note: We intentionally exclude:
	// - actionRequired: so counts don't change when toggling action required
	// - preQualStatus, stage, leadType, models: so counts show total available options
	// - cartTotalRange, svpName: so counts show total available options
	// - dateCreatedRange, lastUpdatedRange: so counts show total available options

	if (Object.keys(filters).length > 0) {
		params.filters = filters;
	}

	return params;
});

// Action atom to handle items per page changes
export const updateItemsPerPageAtom = atom(
	(get) => get(itemsPerPageAtom),
	(get, set, newValue: number) => {
		set(itemsPerPageAtom, newValue);
		// Reset to page 1 when items per page changes
		set(currentPageAtom, 1);
	},
);

// Selection atoms
export const selectedLeadsAtom = atom<Selection>(new Set<string>());
export const isAllMatchingLeadsSelectedAtom = atom<boolean>(false);

// Action atom to handle lead tab filter changes
export const updateLeadTabFilterAtom = atom(
	(get) => get(leadTabFilterAtom),
	(get, set, newTab: LeadsTableTab) => {
		set(leadTabFilterAtom, newTab);
		// Clear selection when changing tab filter
		set(selectedLeadsAtom, new Set<string>());
		set(isAllMatchingLeadsSelectedAtom, false);
		// Reset to first page when changing tab filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle selected leads changes — resets "all matching" when selection changes away from 'all'
export const updateSelectedLeadsAtom = atom(
	(get) => get(selectedLeadsAtom),
	(get, set, newSelection: Selection) => {
		set(selectedLeadsAtom, newSelection);
		if (newSelection !== 'all') {
			set(isAllMatchingLeadsSelectedAtom, false);
		}
	},
);

// Action atom to clear all filters (excluding lead table tab, which is controlled by tabs)
export const clearAllFiltersAtom = atom(null, (get, set) => {
	set(debouncedSearchValueAtom, '');
	set(actionRequiredFilterAtom, false);
	set(preQualStatusFilterAtom, []);
	set(stageFilterAtom, []);
	set(leadTypeFilterAtom, []);
	set(vehicleModelsFilterAtom, []);
	set(dealAmountRangeFilterAtom, null);
	set(accountFilterAtom, []);
	set(dateCreatedFilterAtom, null);
	set(dateCreatedCustomRangeAtom, { from: null, to: null });
	set(lastUpdatedFilterAtom, null);
	set(lastUpdatedCustomRangeAtom, { from: null, to: null });
	set(pipelineFilterAtom, []);
	// Reset to first page when clearing filters
	set(currentPageAtom, 1);
});
